/**
 * Money and time, formatted in one place.
 *
 * The ledger is **integer µUSD** and mandates carry money as **decimal strings**, both because
 * a float cannot be signed reproducibly — a record containing one verifies in Python and fails
 * in JS. So the conversion lives here and nowhere else, and nothing formatted by this module
 * is ever sent back to the API.
 */

/** µUSD (what the ledger stores) → dollars (what a human reads). */
export const microToUsd = (micro: number) => micro / 1_000_000;

/** Dollars (what an operator types) → µUSD. Rounded: the ledger is integer-only, and a
 *  fractional micro-dollar is not a thing. */
export const usdToMicro = (usd: number) => Math.round(usd * 1_000_000);

/**
 * Format money for DISPLAY.
 *
 * Accepts the decimal string the API returns and does not round-trip it through a float where
 * it can be avoided — `Number()` here is the last step before rendering, never before sending.
 */
export function fmtMoney(value: string | number | null | undefined, currency = "USD"): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(n)) return String(value);
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n);
}

/** A timestamp as something a person reads. Absolute past a day: "3 days ago" on an audit row
 *  is worse than a date, because the reader has to know when the page was loaded. */
export function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return String(iso);
  const secs = (Date.now() - then.getTime()) / 1000;
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return then.toISOString().slice(0, 10);
}
